import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Audit stream subscriptions (part 93).
 *
 * This screen is the per-subscriber view that the SSE feed on the Live page deliberately does
 * not provide. Each row is one subscription with its own topic/kind filter and its own cursor;
 * the events panel below shows exactly what that subscriber would receive from its current
 * position. Acknowledging moves only that subscriber's cursor, so two consumers can drain the
 * same audit log at different speeds without seeing each other's events.
 */

type DropPolicy = 'drop_oldest' | 'drop_newest' | 'block'

interface Subscription {
  id: string
  tenant_id: string
  name: string
  topic_pattern: string
  kinds: string[]
  enabled: boolean
  batch_size: number
  max_in_flight: number
  drop_policy: DropPolicy
  created_by?: string
  created_at: string
  updated_at: string
}

interface Cursor {
  subscription_id: string
  cursor: number
  delivered_id: number
  scanned_id: number
  pending?: number[]
  deliveries: number
  redeliveries: number
  acked_events: number
  acks: number
  drops: number
  blocked_polls: number
  gaps: number
  polls: number
  last_polled_at?: string
  last_acked_at?: string
  updated_at: string
}

interface StreamState {
  subscription: Subscription
  cursor: Cursor
  lag_ids: number
  pending_events: number
}

interface Stats {
  subscriptions: number
  enabled: number
  deliveries: number
  redeliveries: number
  acked_events: number
  drops: number
  blocked_polls: number
  gaps: number
  pending_ids: number
  pending_events: number
}

interface Overview {
  subscriptions: StreamState[]
  stats: Stats
  checked_at: string
}

interface StreamEvent {
  id: number
  topic: string
  kind: string
  actor_type?: string
  actor_id?: string
  resource_type?: string
  resource_id?: string
  request_id?: string
  occurred_at: string
  payload: unknown
}

interface StreamPage {
  subscription_id: string
  events: StreamEvent[]
  cursor: Cursor
  delivered: number
  redelivered: number
  dropped: number
  gap: boolean
  blocked: boolean
  examined: number
  has_more: boolean
}

interface AckView {
  subscription_id: string
  cursor: Cursor
  acked: boolean
}

const OVERVIEW_KEY = ['streams']

const policyLabel: Record<DropPolicy, string> = {
  drop_oldest: 'ทิ้งของเก่า',
  drop_newest: 'ทิ้งของใหม่',
  block: 'หยุดรอ',
}

const policyDetail: Record<DropPolicy, string> = {
  drop_oldest: 'เมื่อคิวเกินกำหนด ระบบจะข้ามเหตุการณ์เก่าสุดและส่งของใหม่ให้ ทิ้งมากขึ้นเท่าไรตัวเลข drops จะสูงขึ้น',
  drop_newest: 'เมื่อคิวเกินกำหนด ระบบจะเก็บของเก่าไว้และข้ามเหตุการณ์ใหม่ในรอบนั้น โดยบันทึกจำนวนที่ข้ามไว้',
  block: 'ไม่ส่งเหตุการณ์และไม่เลื่อน cursor เมื่อคิวเต็ม ผู้รับต้องกลับมาดึงใหม่ ตัวเลือกนี้ไม่ทำให้เหตุการณ์หาย',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function kindsText(kinds: string[]): string {
  return kinds.length === 0 ? 'ทุกชนิด' : kinds.join(', ')
}

export function Streams() {
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [pattern, setPattern] = useState('finding.**')
  const [kinds, setKinds] = useState('finding')
  const [batch, setBatch] = useState('100')
  const [maxInFlight, setMaxInFlight] = useState('500')
  const [policy, setPolicy] = useState<DropPolicy>('drop_oldest')
  const [selected, setSelected] = useState<string | null>(null)
  const [page, setPage] = useState<StreamPage | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api<Overview>('/v1/streams'),
    refetchInterval: 15_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<StreamState>('/v1/streams', 'POST', {
      name: name.trim(),
      topic_pattern: pattern.trim(),
      kinds: kinds.split(',').map((value) => value.trim()).filter(Boolean),
      enabled: true,
      batch_size: Number(batch) || 0,
      max_in_flight: Number(maxInFlight) || 0,
      drop_policy: policy,
    }),
    onSuccess: (state) => {
      setNotice(`สร้างผู้รับ ${state.subscription.name} แล้ว · cursor เริ่มที่ 0`)
      setName('')
      create.reset()
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
    },
    onError: (err) => setNotice(errorText(err, 'สร้างผู้รับไม่สำเร็จ')),
  })

  const poll = useMutation({
    mutationFn: (id: string) => api<StreamPage>(`/v1/streams/${encodeURIComponent(id)}/events?limit=50`),
    onSuccess: (result, id) => {
      setSelected(id)
      setPage(result)
      setNotice(
        result.blocked
          ? 'คิวเต็มตามนโยบายหยุดรอ · ยังไม่มีการส่งหรือเลื่อน cursor'
          : `ดึงได้ ${result.delivered} เหตุการณ์ · สแกน ${result.examined} แถว`,
      )
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
    },
    onError: (err) => setNotice(errorText(err, 'ดึงเหตุการณ์ไม่สำเร็จ')),
  })

  const ack = useMutation({
    mutationFn: (result: StreamPage) => apiWrite<AckView>(
      `/v1/streams/${encodeURIComponent(result.subscription_id)}/ack`,
      'POST',
      { cursor: result.cursor.delivered_id },
    ),
    onSuccess: (result) => {
      setPage((current) => (current && current.subscription_id === result.subscription_id
        ? { ...current, cursor: result.cursor }
        : current))
      setNotice(result.acked
        ? `ยืนยัน cursor ถึง ${result.cursor.cursor} แล้ว · รอบถัดไปจะไม่ส่งซ้ำ`
        : 'cursor นี้ถูกยืนยันไว้แล้ว ไม่มีการเปลี่ยนแปลง')
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
    },
    onError: (err) => setNotice(errorText(err, 'ยืนยัน cursor ไม่สำเร็จ')),
  })

  const rows = overview.data?.subscriptions ?? []
  const stats = overview.data?.stats
  const selectedRow = rows.find((row) => row.subscription.id === selected)
  const canCreate = name.trim().length > 0 && pattern.trim().length > 0 && !create.isPending

  const head = (
    <PageHead
      title="สตรีมเหตุการณ์ตรวจสอบ"
      sub="ผู้รับแต่ละรายมีตัวกรองหัวข้อและชนิดเหตุการณ์ของตนเอง พร้อม cursor ที่เลื่อนเมื่อยืนยันเท่านั้น · เหตุการณ์ที่ส่งแล้วแต่ยังไม่ยืนยันจะถูกส่งซ้ำ และจะไม่ถูกส่งซ้ำอีกหลังยืนยันแล้ว"
    />
  )

  if (overview.isLoading) {
    return (
      <>
        {head}
        <div className="stack"><Skel h={110} /><Skel h={300} /></div>
      </>
    )
  }

  if (overview.isError) {
    return (
      <>
        {head}
        <Empty title="โหลดรายการสตรีมไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  return (
    <>
      {head}

      <div className="grid-4 stagger">
        <Stat
          label="ผู้รับทั้งหมด"
          value={stats?.subscriptions ?? 0}
          unit="ราย"
          foot={`เปิดใช้งาน ${number(stats?.enabled ?? 0)} ราย · รีเฟรชทุก 15 วินาที`}
        />
        <Stat
          label="รอการยืนยัน"
          value={number(stats?.pending_events ?? 0)}
          unit="เหตุการณ์"
          foot={`ช่วงรหัสที่ยังไม่ปิด ${number(stats?.pending_ids ?? 0)} แถว`}
        />
        <Stat
          label="การส่งสะสม"
          value={number(stats?.deliveries ?? 0)}
          unit="ครั้ง"
          foot={`ส่งซ้ำ ${number(stats?.redeliveries ?? 0)} ครั้ง · ยืนยันแล้ว ${number(stats?.acked_events ?? 0)} รายการ`}
        />
        <Stat
          label="เหตุการณ์ที่ถูกทิ้ง"
          value={number(stats?.drops ?? 0)}
          unit="รายการ"
          foot={`ช่องว่าง ${number(stats?.gaps ?? 0)} ครั้ง · หยุดรอ ${number(stats?.blocked_polls ?? 0)} รอบ`}
        />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div className="card-label">ผู้รับในองค์กรนี้</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                เลือกผู้รับเพื่อดูเหตุการณ์จาก cursor ปัจจุบัน · แสดง {rows.length} รายการ
              </div>
            </div>
            <button
              className="btn btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => void overview.refetch()}
              disabled={overview.isFetching}
            >
              {overview.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
            </button>
          </div>

          <div className="rule" style={{ margin: '14px 0 0' }} />

          {rows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่มีผู้รับเหตุการณ์"
                body="สร้างผู้รับทางขวาเพื่อกำหนดหัวข้อ ชนิดเหตุการณ์ และนโยบายเมื่อคิวเต็ม ผู้รับจะเริ่มที่ cursor 0 และเห็นเฉพาะเหตุการณ์ที่ตรงกับตัวกรอง"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ชื่อผู้รับ</th>
                    <th>ตัวกรอง</th>
                    <th style={{ width: 110 }}>นโยบาย</th>
                    <th style={{ width: 150 }}>cursor</th>
                    <th style={{ width: 90 }}>ค้าง</th>
                    <th style={{ width: 110 }}>สถิติ</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const sub = row.subscription
                    const active = sub.id === selected
                    return (
                      <tr
                        key={sub.id}
                        onClick={() => {
                          setSelected(sub.id)
                          setPage((current) => (current?.subscription_id === sub.id ? current : null))
                        }}
                        style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                      >
                        <td>
                          <div>{sub.name}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                            {sub.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'} · {sub.created_by || 'system'}
                          </div>
                        </td>
                        <td>
                          <div className="mono">{sub.topic_pattern}</div>
                          <div className="row" style={{ flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {(sub.kinds.length === 0 ? ['ทุกชนิด'] : sub.kinds).map((kind) => (
                              <span className="chip" key={kind}>{kind}</span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div>{policyLabel[sub.drop_policy]}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                            {sub.batch_size}/{number(sub.max_in_flight)}
                          </div>
                        </td>
                        <td className="mono tnum">
                          <div>#{number(row.cursor.cursor)}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            ส่งถึง #{number(row.cursor.delivered_id)}
                          </div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            สแกนถึง #{number(row.cursor.scanned_id)}
                          </div>
                        </td>
                        <td>
                          <div className={`tnum ${row.pending_events > 0 ? 'sev-high' : 'dim'}`}>
                            {number(row.pending_events)}
                          </div>
                          <div className="dim" style={{ fontSize: 10.5 }}>{number(row.lag_ids)} แถว</div>
                        </td>
                        <td className="mono tnum">
                          <div>{number(row.cursor.deliveries)}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>ทิ้ง {number(row.cursor.drops)}</div>
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm"
                              onClick={(event) => {
                                event.stopPropagation()
                                poll.mutate(sub.id)
                              }}
                              disabled={poll.isPending || !sub.enabled}
                            >
                              {poll.isPending && poll.variables === sub.id ? 'กำลังดึง…' : 'ดึงเหตุการณ์'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {notice ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>{notice}</div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">สร้างผู้รับใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
            รูปแบบหัวข้อใช้ * แทนหนึ่งระดับ และ ** แทนทุกระดับที่เหลือ เช่น finding.** หรือ **
          </div>

          <div className="field">
            <label htmlFor="stream-name">ชื่อผู้รับ</label>
            <input
              id="stream-name"
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="ทีมตรวจสอบการฉ้อโกง"
              maxLength={120}
            />
          </div>

          <div className="field">
            <label htmlFor="stream-pattern">รูปแบบหัวข้อ</label>
            <input
              id="stream-pattern"
              className="input mono"
              value={pattern}
              onChange={(event) => setPattern(event.target.value)}
              placeholder="finding.**"
              maxLength={120}
            />
          </div>

          <div className="field">
            <label htmlFor="stream-kinds">ชนิดเหตุการณ์ (คั่นด้วยจุลภาค)</label>
            <input
              id="stream-kinds"
              className="input mono"
              value={kinds}
              onChange={(event) => setKinds(event.target.value)}
              placeholder="finding, document"
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
              ชนิดคือ resource_type ของเหตุการณ์ · เว้นว่างเพื่อรับทุกชนิด
            </div>
          </div>

          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="stream-batch">ต่อรอบ</label>
              <input
                id="stream-batch"
                className="input tnum"
                type="number"
                min={1}
                max={200}
                value={batch}
                onChange={(event) => setBatch(event.target.value)}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="stream-inflight">คิวสูงสุด</label>
              <input
                id="stream-inflight"
                className="input tnum"
                type="number"
                min={1}
                max={5000}
                value={maxInFlight}
                onChange={(event) => setMaxInFlight(event.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="stream-policy">นโยบายเมื่อคิวเต็ม</label>
            <select
              id="stream-policy"
              className="select"
              value={policy}
              onChange={(event) => setPolicy(event.target.value as DropPolicy)}
            >
              <option value="drop_oldest">ทิ้งของเก่า · ส่งของใหม่</option>
              <option value="drop_newest">ทิ้งของใหม่ · เก็บของเก่า</option>
              <option value="block">หยุดรอ · ไม่ทิ้งเหตุการณ์</option>
            </select>
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>{policyDetail[policy]}</div>
          </div>

          <button
            className="btn"
            style={{ marginTop: 18, width: '100%' }}
            onClick={() => create.mutate()}
            disabled={!canCreate}
          >
            {create.isPending ? 'กำลังสร้าง…' : 'สร้างผู้รับ'}
          </button>
        </div>
      </div>

      {selectedRow ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div className="card-label">เหตุการณ์จาก cursor ของ {selectedRow.subscription.name}</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                หัวข้อ {selectedRow.subscription.topic_pattern} · ชนิด {kindsText(selectedRow.subscription.kinds)}
              </div>
            </div>
            <div className="page-actions">
              <button
                className="btn btn-sm"
                onClick={() => poll.mutate(selectedRow.subscription.id)}
                disabled={poll.isPending}
              >
                {poll.isPending ? 'กำลังดึง…' : 'ดึงจาก cursor ปัจจุบัน'}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => page && ack.mutate(page)}
                disabled={!page || ack.isPending || page.cursor.delivered_id === 0}
              >
                {ack.isPending ? 'กำลังยืนยัน…' : 'ยืนยัน cursor'}
              </button>
            </div>
          </div>

          {page ? (
            <>
              <div className="row" style={{ flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>ส่ง {number(page.delivered)}</span>
                <span className="dim" style={{ fontSize: 11.5 }}>ส่งซ้ำ {number(page.redelivered)}</span>
                <span className="dim" style={{ fontSize: 11.5 }}>ทิ้ง {number(page.dropped)}</span>
                <span className="dim" style={{ fontSize: 11.5 }}>สแกน {number(page.examined)} แถว</span>
                {page.has_more ? <span className="dim" style={{ fontSize: 11.5 }}>ยังมีต่อ</span> : null}
              </div>
              {page.blocked ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                  คิวเต็มและนโยบายเป็นหยุดรอ · ระบบไม่ส่งเหตุการณ์และไม่เลื่อน cursor
                </div>
              ) : null}
              {page.gap ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                  มีช่องว่างจากการทิ้งเหตุการณ์ {number(page.dropped)} รายการในช่วงนี้ · ตัวเลขนี้เป็นเหตุผลที่ผู้รับไม่ได้เห็นเหตุการณ์เหล่านั้น
                </div>
              ) : null}

              {page.events.length === 0 ? (
                <div style={{ marginTop: 14 }}>
                  <Empty
                    title="ไม่มีเหตุการณ์ใหม่ที่ตรงกับตัวกรอง"
                    body="ตำแหน่งปัจจุบันอยู่หลังเหตุการณ์ที่ตรงทั้งหมดแล้ว · เมื่อมีเหตุการณ์ใหม่ ผู้รับจะเห็นในรอบถัดไป"
                  />
                </div>
              ) : (
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="table data">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>รหัส</th>
                        <th>หัวข้อ</th>
                        <th style={{ width: 110 }}>ชนิด</th>
                        <th style={{ width: 170 }}>เวลา</th>
                        <th style={{ width: 150 }}>ผู้กระทำ</th>
                        <th>ข้อมูล</th>
                      </tr>
                    </thead>
                    <tbody>
                      {page.events.map((ev) => (
                        <tr key={ev.id} style={{ cursor: 'default' }}>
                          <td className="mono tnum">#{number(ev.id)}</td>
                          <td className="mono">{ev.topic}</td>
                          <td><span className="chip">{ev.kind || 'ไม่ระบุ'}</span></td>
                          <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(ev.occurred_at)}</td>
                          <td className="mono" style={{ fontSize: 11 }}>
                            {ev.actor_type || 'system'}
                            {ev.actor_id ? ` · ${ev.actor_id.slice(0, 8)}` : ''}
                          </td>
                          <td className="mono" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {JSON.stringify(ev.payload)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่ได้ดึงเหตุการณ์ของผู้รับนี้"
                body="กดปุ่มดึงจาก cursor ปัจจุบันเพื่อดูเพจแรก · หลังยืนยัน cursor แล้ว รอบถัดไปจะเริ่มหลังเหตุการณ์ที่ยืนยันโดยไม่ส่งซ้ำ"
              />
            </div>
          )}
        </div>
      ) : null}
    </>
  )
}
